import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { collection, doc, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../../Firebase";
import "../../Admin.css"; // reuse list page styles (optional)
import AdminNav from "../../components/AdminNav";

const TZ = "America/Chicago";

function isoToday() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function addDaysIso(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function linesToArray(text) {
  return String(text || "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
}

function arrayToLines(arr) {
  return (Array.isArray(arr) ? arr : []).join("\n");
}

function newItem(id) {
  return {
    itemId: id,
    category: "Repairs",
    name: "",
    description: "",
    qty: 1,
    unit: "ea",
    unitPrice: 0,
    taxable: true,
    references: [],
    sortOrder: 1,
    photos: [],
    costBreakdown: {
      materials: [],
      labor: { hours: 0, rate: 0 },
      equipment: [],
      subcontract: [],
    },
  };
}

function newGroup(id) {
  return { groupId: id, title: "", notes: "", items: [newItem(`${id}-001`)] };
}

function newOption(optionId) {
  return {
    optionId,
    title: optionId === "A" ? "Option A — Targeted Repairs" : `Option ${optionId}`,
    summary: "",
    includedText: "",
    excludedText: "",
    areas: [newGroup(`G-${optionId}-01`)],
    totals: {
      subtotal: 0,
      taxableSubtotal: 0,
      taxTotal: 0,
      discountTotal: 0,
      feesTotal: 0,
      grandTotal: 0,
    },
  };
}

function computeOptionTotals(option, pricing) {
  const taxRate = safeNum(pricing.taxRate);
  const taxMode = pricing.taxMode || "exclusive";

  const discounts = Array.isArray(pricing.discounts) ? pricing.discounts : [];
  const fees = Array.isArray(pricing.fees) ? pricing.fees : [];

  let subtotal = 0;
  let taxableSubtotal = 0;

  for (const g of option.areas || []) {
    for (const it of g.items || []) {
      const qty = safeNum(it.qty);
      const unitPrice = safeNum(it.unitPrice);
      const line = qty * unitPrice;
      subtotal += line;
      if (it.taxable) taxableSubtotal += line;
    }
  }

  // Fees
  let feesTotal = 0;
  let taxableFees = 0;
  for (const f of fees) {
    const amt = safeNum(f.amount);
    feesTotal += amt;
    if (f.taxable) taxableFees += amt;
  }

  // Discounts (applied to subtotal; then allocate portionally to taxable)
  let discountTotal = 0;
  for (const d of discounts) {
    const type = d.type || "percent";
    const value = safeNum(d.value);
    if (type === "percent") discountTotal += subtotal * value;
    else discountTotal += value;
  }

  // Keep discounts from exceeding subtotal
  discountTotal = Math.max(0, Math.min(discountTotal, subtotal));

  const taxableRatio = subtotal > 0 ? taxableSubtotal / subtotal : 0;
  const taxableDiscountShare = discountTotal * taxableRatio;

  const taxableAfterDiscount = Math.max(0, taxableSubtotal - taxableDiscountShare);
  const subtotalAfterDiscount = Math.max(0, subtotal - discountTotal);

  let taxTotal = 0;
  if (taxMode === "exclusive") {
    taxTotal = (taxableAfterDiscount + taxableFees) * taxRate;
  } else if (taxMode === "inclusive") {
    // Inclusive tax math is trickier; for v1 of your "full-fat" model,
    // we'll treat it as zero additional tax line (client sees tax included).
    taxTotal = 0;
  } else {
    taxTotal = 0;
  }

  const grandTotal = subtotalAfterDiscount + feesTotal + taxTotal;

  return {
    subtotal,
    taxableSubtotal,
    discountTotal,
    feesTotal,
    taxTotal,
    grandTotal,
  };
}

export default function AdminEstimateCreate() {
  const nav = useNavigate();

  const [saving, setSaving] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // We'll generate doc ID on first save, then redirect to edit
  const [blobId, setBlobId] = useState("");

  const [form, setForm] = useState(() => ({
    status: "draft",
    estimateNumber: "",
    revision: 1,
    revisionOf: null,

    validUntil: addDaysIso(30),
    currency: "USD",
    language: "en-US",
    timezone: TZ,
    tagsText: "commercial\ninspection-led",

    customer: {
      type: "company",
      companyName: "",
      name: "",
      phone: "",
      email: "",
      billingAddress: {
        line1: "",
        line2: "",
        city: "",
        state: "TX",
        postal: "",
        country: "US",
      },
      contacts: [],
    },

    site: {
      propertyName: "",
      siteAddress: {
        line1: "",
        line2: "",
        city: "",
        state: "TX",
        postal: "",
        country: "US",
      },
      access: "",
      siteConstraintsText: "",
      safetyRequirementsText: "",
      roofSystemsText: "Standing Seam Metal",
      approxArea: "",
      notes: "",
    },

    issuer: {
      companyName: "Crowned Roofing",
      address: {
        line1: "Houston, TX",
        line2: "",
        city: "Houston",
        state: "TX",
        postal: "",
        country: "US",
      },
      phone: "",
      email: "estimates@crownedroofing.com",
      website: "crownedroofing.com",
      license: "",
      preparedBy: { name: "", title: "" },
    },

    pricing: {
      taxMode: "exclusive",
      taxRate: 0.0825,
      discounts: [{ label: "Preferred Vendor Discount", type: "percent", value: 0.0 }],
      fees: [{ label: "Mobilization", amount: 0, taxable: false }],
      rounding: "nearest_cent",
    },

    options: [newOption("A"), newOption("B")],

    schedule: {
      leadTime: "7–10 business days from written approval and deposit (if applicable).",
      duration: "1–2 days on site (weather permitting).",
      workWindow: "Mon–Fri 7:00am–5:00pm",
      milestonesText:
        "Pre-job coordination — Confirm access and escort requirements.\nOn-site work — Perform scope per selected option.\nCloseout — Completion photos and summary provided.",
    },

    payment: {
      depositRequired: true,
      depositType: "percent",
      depositValue: 0.3,
      paymentMethodsText: "Check\nACH\nCard",
      invoicing: "Balance due upon completion unless otherwise agreed in writing.",
      lateFeePolicy: "Past due balances may be subject to late fees as permitted by applicable law.",
    },

    warranty: {
      type: "Workmanship",
      term: "12 months",
      detailsText:
        "Warranty applies to workmanship for scope described under the selected option.\nDoes not cover damage due to severe weather, third-party trades, or building movement.\nWarranty contingent upon safe roof access and conditions.",
    },

    assumptionsText:
      "Estimate based on visual inspection and accessible roof areas.\nConcealed conditions may exist; additional work may be required if discovered.\nWork scheduled weather permitting.",

    exclusionsText:
      "Interior repairs (ceilings, insulation, flooring, electrical, etc.).\nStructural repairs or deck replacement unless explicitly included.\nPermit fees unless explicitly included.\nHazardous material abatement (if present).",

    clarificationsText:
      "Sealant systems used will be compatible with the existing roof system.\nOwner/GC to provide roof access and escorts if required.\nWork outside listed scope requires written authorization.",

    termsText:
      "This estimate is valid through the Valid Until date listed above.\nWork will be performed in a professional manner consistent with industry standards.\nClient approval indicates acceptance of selected option scope, pricing, and terms.",

    attachments: [],
    publicAccess: { enabled: true, requireEmailGate: false, pin: "" },
    acceptance: {
      selectedOptionId: "",
      acceptedByName: "",
      acceptedByEmail: "",
      acceptedByTitle: "",
      signature: { path: "", signedAt: "" },
      ipAddress: "",
      userAgent: "",
    },
    audit: [
      { at: isoToday(), type: "created", by: "admin", note: "Estimate created." }
    ],
  }));

  useEffect(() => {
    document.title = "New Estimate | Admin";
  }, []);

  // Derived totals (kept live)
  const optionsWithTotals = useMemo(() => {
    const pricing = form.pricing;
    return (form.options || []).map((opt) => ({
      ...opt,
      totals: computeOptionTotals(opt, pricing),
    }));
  }, [form.options, form.pricing]);

  function setField(path, value) {
    setForm((prev) => {
      const next = structuredClone(prev);
      const parts = path.split(".");
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) cur = cur[parts[i]];
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  }

  function updateOption(idx, patch) {
    setForm((prev) => {
      const next = structuredClone(prev);
      next.options[idx] = { ...next.options[idx], ...patch };
      return next;
    });
  }

  function updateGroup(optionIdx, groupIdx, patch) {
    setForm((prev) => {
      const next = structuredClone(prev);
      next.options[optionIdx].areas[groupIdx] = {
        ...next.options[optionIdx].areas[groupIdx],
        ...patch,
      };
      return next;
    });
  }

  function updateItem(optionIdx, groupIdx, itemIdx, patch) {
    setForm((prev) => {
      const next = structuredClone(prev);
      const it = next.options[optionIdx].areas[groupIdx].items[itemIdx];
      next.options[optionIdx].areas[groupIdx].items[itemIdx] = { ...it, ...patch };
      return next;
    });
  }

  function addOption() {
    setForm((prev) => {
      const next = structuredClone(prev);
      const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");
      const used = new Set((next.options || []).map((o) => o.optionId));
      const nextId = letters.find((c) => !used.has(c)) || `ALT${(next.options?.length || 0) + 1}`;
      next.options.push(newOption(nextId));
      return next;
    });
  }

  function removeOption(idx) {
    setForm((prev) => {
      const next = structuredClone(prev);
      next.options.splice(idx, 1);
      return next;
    });
  }

  function addGroup(optionIdx) {
    setForm((prev) => {
      const next = structuredClone(prev);
      const opt = next.options[optionIdx];
      const n = (opt.areas?.length || 0) + 1;
      opt.areas.push(newGroup(`G-${opt.optionId}-${String(n).padStart(2, "0")}`));
      return next;
    });
  }

  function removeGroup(optionIdx, groupIdx) {
    setForm((prev) => {
      const next = structuredClone(prev);
      next.options[optionIdx].areas.splice(groupIdx, 1);
      return next;
    });
  }

  function addItem(optionIdx, groupIdx) {
    setForm((prev) => {
      const next = structuredClone(prev);
      const group = next.options[optionIdx].areas[groupIdx];
      const n = (group.items?.length || 0) + 1;
      group.items.push(newItem(`${group.groupId}-${String(n).padStart(3, "0")}`));
      return next;
    });
  }

  function removeItem(optionIdx, groupIdx, itemIdx) {
    setForm((prev) => {
      const next = structuredClone(prev);
      next.options[optionIdx].areas[groupIdx].items.splice(itemIdx, 1);
      return next;
    });
  }

  function toFirestorePayload(nextStatus) {
    const payload = structuredClone(form);

    payload.status = nextStatus || payload.status || "draft";

    // Normalize multiline -> arrays
    payload.tags = linesToArray(payload.tagsText);
    delete payload.tagsText;

    payload.site.siteConstraints = linesToArray(payload.site.siteConstraintsText);
    payload.site.safetyRequirements = linesToArray(payload.site.safetyRequirementsText);
    payload.site.roofSystems = linesToArray(payload.site.roofSystemsText);
    delete payload.site.siteConstraintsText;
    delete payload.site.safetyRequirementsText;
    delete payload.site.roofSystemsText;

    payload.schedule.milestones = linesToArray(payload.schedule.milestonesText).map((line) => {
      // Allow "Label — Notes" shorthand
      const parts = line.split("—");
      return {
        label: (parts[0] || "").trim(),
        dateTarget: "",
        notes: (parts[1] || "").trim(),
      };
    });
    delete payload.schedule.milestonesText;

    payload.payment.paymentMethods = linesToArray(payload.payment.paymentMethodsText);
    delete payload.payment.paymentMethodsText;

    payload.warranty.details = linesToArray(payload.warranty.detailsText);
    delete payload.warranty.detailsText;

    payload.assumptions = linesToArray(payload.assumptionsText);
    payload.exclusions = linesToArray(payload.exclusionsText);
    payload.clarifications = linesToArray(payload.clarificationsText);
    payload.terms = linesToArray(payload.termsText);
    delete payload.assumptionsText;
    delete payload.exclusionsText;
    delete payload.clarificationsText;
    delete payload.termsText;

    // Put computed totals into each option
    payload.options = optionsWithTotals.map((o) => ({
      optionId: o.optionId,
      title: o.title,
      summary: o.summary,
      included: linesToArray(o.includedText),
      excluded: linesToArray(o.excludedText),
      areas: (o.areas || []).map((g) => ({
        groupId: g.groupId,
        title: g.title,
        notes: g.notes,
        items: (g.items || []).map((it) => ({
          itemId: it.itemId,
          category: it.category,
          name: it.name,
          description: it.description,
          qty: safeNum(it.qty),
          unit: it.unit,
          unitPrice: safeNum(it.unitPrice),
          taxable: !!it.taxable,
          references: Array.isArray(it.references) ? it.references : [],
          sortOrder: safeNum(it.sortOrder),
          photos: Array.isArray(it.photos) ? it.photos : [],
          costBreakdown: it.costBreakdown || {
            materials: [],
            labor: { hours: 0, rate: 0 },
            equipment: [],
            subcontract: [],
          },
        })),
      })),
      totals: o.totals,
    }));

    // timestamps
    payload.updatedAt = serverTimestamp();
    payload.createdAt = serverTimestamp();

    // sent/accepted timestamps (optional)
    if (payload.status === "sent") payload.sentAt = serverTimestamp();

    return payload;
  }

  async function saveFirst(nextStatus) {
    setSaving(true);
    setErrorMsg("");
    setStatusMsg("");

    try {
      // Create new doc ref once
      const col = collection(db, "publicEstimates");
      const newRef = doc(col); // auto id
      const payload = toFirestorePayload(nextStatus);

      await setDoc(newRef, payload, { merge: false });

      setBlobId(newRef.id);
      setStatusMsg("Estimate created.");
      // Immediately jump to edit page for further changes
      nav(`/admin/estimates/edit/${newRef.id}`);
    } catch (e) {
      setErrorMsg(e?.message || "Failed to create estimate.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="aePage">
      <AdminNav />
      <div className="aeTop">
        <div>
          <h1>New Estimate</h1>
          <p className="aeSub">
            Full estimate document with options, grouped scope, and print totals.
          </p>
        </div>

        <div className="aeActions">
          <Link className="btn btnGhost" to="/admin/estimates">
            ← Back
          </Link>
          <button className="btn" disabled={saving} onClick={() => saveFirst("draft")}>
            Save Draft
          </button>
          <button className="btn btnPrimary" disabled={saving} onClick={() => saveFirst("sent")}>
            Create + Mark Sent
          </button>
        </div>
      </div>

      {statusMsg ? <div className="aeError" style={{ background: "#ecfeff", color: "#155e75", borderColor: "rgba(6,182,212,.25)" }}>{statusMsg}</div> : null}
      {errorMsg ? <div className="aeError">{errorMsg}</div> : null}

      {/* IDENTITY */}
      <div className="aeTableWrap" style={{ padding: 16, marginBottom: 14 }}>
        <h3 style={{ margin: "0 0 8px" }}>Identity</h3>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <label>
            <div className="aeSmall">Estimate Number (optional)</div>
            <input
              className="aeSearch"
              value={form.estimateNumber}
              onChange={(e) => setField("estimateNumber", e.target.value)}
              placeholder="CR-EST-2026-0017"
            />
          </label>

          <label>
            <div className="aeSmall">Valid Until</div>
            <input
              className="aeSearch"
              type="date"
              value={form.validUntil}
              onChange={(e) => setField("validUntil", e.target.value)}
            />
          </label>

          <label>
            <div className="aeSmall">Currency</div>
            <input
              className="aeSearch"
              value={form.currency}
              onChange={(e) => setField("currency", e.target.value)}
              placeholder="USD"
            />
          </label>
        </div>

        <div style={{ marginTop: 10 }}>
          <div className="aeSmall">Tags (one per line)</div>
          <textarea
            className="aeSearch"
            style={{ minHeight: 80 }}
            value={form.tagsText}
            onChange={(e) => setField("tagsText", e.target.value)}
          />
        </div>
      </div>

      {/* CUSTOMER */}
      <div className="aeTableWrap" style={{ padding: 16, marginBottom: 14 }}>
        <h3 style={{ margin: "0 0 8px" }}>Customer</h3>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <label>
            <div className="aeSmall">Customer Type</div>
            <select
              className="aeSearch"
              value={form.customer.type}
              onChange={(e) => setField("customer.type", e.target.value)}
            >
              <option value="company">Company</option>
              <option value="individual">Individual</option>
            </select>
          </label>

          <label>
            <div className="aeSmall">Company Name</div>
            <input
              className="aeSearch"
              value={form.customer.companyName}
              onChange={(e) => setField("customer.companyName", e.target.value)}
            />
          </label>

          <label>
            <div className="aeSmall">Primary Contact Name</div>
            <input
              className="aeSearch"
              value={form.customer.name}
              onChange={(e) => setField("customer.name", e.target.value)}
            />
          </label>

          <label>
            <div className="aeSmall">Phone</div>
            <input
              className="aeSearch"
              value={form.customer.phone}
              onChange={(e) => setField("customer.phone", e.target.value)}
            />
          </label>

          <label>
            <div className="aeSmall">Email</div>
            <input
              className="aeSearch"
              value={form.customer.email}
              onChange={(e) => setField("customer.email", e.target.value)}
            />
          </label>
        </div>

        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 12 }}>
          <label>
            <div className="aeSmall">Billing Address Line 1</div>
            <input
              className="aeSearch"
              value={form.customer.billingAddress.line1}
              onChange={(e) => setField("customer.billingAddress.line1", e.target.value)}
            />
          </label>
          <label>
            <div className="aeSmall">City</div>
            <input
              className="aeSearch"
              value={form.customer.billingAddress.city}
              onChange={(e) => setField("customer.billingAddress.city", e.target.value)}
            />
          </label>
          <label>
            <div className="aeSmall">State</div>
            <input
              className="aeSearch"
              value={form.customer.billingAddress.state}
              onChange={(e) => setField("customer.billingAddress.state", e.target.value)}
            />
          </label>
          <label>
            <div className="aeSmall">ZIP</div>
            <input
              className="aeSearch"
              value={form.customer.billingAddress.postal}
              onChange={(e) => setField("customer.billingAddress.postal", e.target.value)}
            />
          </label>
        </div>
      </div>

      {/* SITE */}
      <div className="aeTableWrap" style={{ padding: 16, marginBottom: 14 }}>
        <h3 style={{ margin: "0 0 8px" }}>Site</h3>

        <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1.8fr", gap: 12 }}>
          <label>
            <div className="aeSmall">Property Name</div>
            <input
              className="aeSearch"
              value={form.site.propertyName}
              onChange={(e) => setField("site.propertyName", e.target.value)}
            />
          </label>

          <label>
            <div className="aeSmall">Site Address Line 1</div>
            <input
              className="aeSearch"
              value={form.site.siteAddress.line1}
              onChange={(e) => setField("site.siteAddress.line1", e.target.value)}
            />
          </label>
        </div>

        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12 }}>
          <label>
            <div className="aeSmall">City</div>
            <input
              className="aeSearch"
              value={form.site.siteAddress.city}
              onChange={(e) => setField("site.siteAddress.city", e.target.value)}
            />
          </label>
          <label>
            <div className="aeSmall">State</div>
            <input
              className="aeSearch"
              value={form.site.siteAddress.state}
              onChange={(e) => setField("site.siteAddress.state", e.target.value)}
            />
          </label>
          <label>
            <div className="aeSmall">ZIP</div>
            <input
              className="aeSearch"
              value={form.site.siteAddress.postal}
              onChange={(e) => setField("site.siteAddress.postal", e.target.value)}
            />
          </label>
          <label>
            <div className="aeSmall">Approx. Area</div>
            <input
              className="aeSearch"
              value={form.site.approxArea}
              onChange={(e) => setField("site.approxArea", e.target.value)}
              placeholder="98,000 sqft"
            />
          </label>
        </div>

        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label>
            <div className="aeSmall">Roof Systems (one per line)</div>
            <textarea
              className="aeSearch"
              style={{ minHeight: 70 }}
              value={form.site.roofSystemsText}
              onChange={(e) => setField("site.roofSystemsText", e.target.value)}
            />
          </label>
          <label>
            <div className="aeSmall">Access</div>
            <textarea
              className="aeSearch"
              style={{ minHeight: 70 }}
              value={form.site.access}
              onChange={(e) => setField("site.access", e.target.value)}
              placeholder="Ladder access; escort required…"
            />
          </label>
        </div>

        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label>
            <div className="aeSmall">Site Constraints (one per line)</div>
            <textarea
              className="aeSearch"
              style={{ minHeight: 90 }}
              value={form.site.siteConstraintsText}
              onChange={(e) => setField("site.siteConstraintsText", e.target.value)}
            />
          </label>
          <label>
            <div className="aeSmall">Safety Requirements (one per line)</div>
            <textarea
              className="aeSearch"
              style={{ minHeight: 90 }}
              value={form.site.safetyRequirementsText}
              onChange={(e) => setField("site.safetyRequirementsText", e.target.value)}
            />
          </label>
        </div>
      </div>

      {/* PRICING */}
      <div className="aeTableWrap" style={{ padding: 16, marginBottom: 14 }}>
        <h3 style={{ margin: "0 0 8px" }}>Pricing</h3>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <label>
            <div className="aeSmall">Tax Mode</div>
            <select
              className="aeSearch"
              value={form.pricing.taxMode}
              onChange={(e) => setField("pricing.taxMode", e.target.value)}
            >
              <option value="exclusive">Exclusive (adds tax line)</option>
              <option value="inclusive">Inclusive (tax included)</option>
              <option value="none">No tax</option>
            </select>
          </label>

          <label>
            <div className="aeSmall">Tax Rate</div>
            <input
              className="aeSearch"
              value={form.pricing.taxRate}
              onChange={(e) => setField("pricing.taxRate", e.target.value)}
              placeholder="0.0825"
            />
          </label>

          <label>
            <div className="aeSmall">Fee — Mobilization</div>
            <input
              className="aeSearch"
              value={form.pricing.fees?.[0]?.amount ?? 0}
              onChange={(e) => {
                const v = e.target.value;
                setForm((prev) => {
                  const next = structuredClone(prev);
                  next.pricing.fees[0].amount = safeNum(v);
                  return next;
                });
              }}
              placeholder="0"
            />
          </label>
        </div>

        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 12 }}>
          <label>
            <div className="aeSmall">Discount Label</div>
            <input
              className="aeSearch"
              value={form.pricing.discounts?.[0]?.label ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                setForm((prev) => {
                  const next = structuredClone(prev);
                  next.pricing.discounts[0].label = v;
                  return next;
                });
              }}
            />
          </label>

          <label>
            <div className="aeSmall">Discount Type</div>
            <select
              className="aeSearch"
              value={form.pricing.discounts?.[0]?.type ?? "percent"}
              onChange={(e) => {
                const v = e.target.value;
                setForm((prev) => {
                  const next = structuredClone(prev);
                  next.pricing.discounts[0].type = v;
                  return next;
                });
              }}
            >
              <option value="percent">Percent</option>
              <option value="fixed">Fixed</option>
            </select>
          </label>

          <label>
            <div className="aeSmall">Discount Value</div>
            <input
              className="aeSearch"
              value={form.pricing.discounts?.[0]?.value ?? 0}
              onChange={(e) => {
                const v = e.target.value;
                setForm((prev) => {
                  const next = structuredClone(prev);
                  next.pricing.discounts[0].value = safeNum(v);
                  return next;
                });
              }}
              placeholder="0.03 for 3%"
            />
          </label>
        </div>
      </div>

      {/* OPTIONS + SCOPE */}
      <div className="aeTableWrap" style={{ padding: 16, marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
          <h3 style={{ margin: 0 }}>Options & Scope</h3>
          <button className="btn btnGhost" type="button" onClick={addOption}>
            + Add option
          </button>
        </div>

        <div style={{ marginTop: 10, display: "grid", gap: 14 }}>
          {form.options.map((opt, optionIdx) => {
            const computed = optionsWithTotals[optionIdx]?.totals;
            return (
              <div key={opt.optionId} style={{ border: "1px solid rgba(15,23,42,.10)", borderRadius: 14, padding: 12 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                  <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                    <div className="pill info">{opt.optionId}</div>
                    <strong>{opt.title}</strong>
                  </div>

                  <button
                    className="btn btnTiny btnGhost"
                    type="button"
                    onClick={() => removeOption(optionIdx)}
                    disabled={form.options.length <= 1}
                  >
                    Remove option
                  </button>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12, marginTop: 10 }}>
                  <label>
                    <div className="aeSmall">Title</div>
                    <input
                      className="aeSearch"
                      value={opt.title}
                      onChange={(e) => updateOption(optionIdx, { title: e.target.value })}
                    />
                  </label>

                  <div style={{ display: "grid", gap: 6 }}>
                    <div className="aeSmall">Totals (computed)</div>
                    <div className="aeSmall">
                      Subtotal: <strong>{Math.round(computed.subtotal).toLocaleString()}</strong>{" "}
                      | Tax: <strong>{Math.round(computed.taxTotal).toLocaleString()}</strong>{" "}
                      | Grand: <strong>{Math.round(computed.grandTotal).toLocaleString()}</strong>
                    </div>
                  </div>
                </div>

                <label style={{ display: "block", marginTop: 10 }}>
                  <div className="aeSmall">Summary</div>
                  <textarea
                    className="aeSearch"
                    style={{ minHeight: 70 }}
                    value={opt.summary}
                    onChange={(e) => updateOption(optionIdx, { summary: e.target.value })}
                  />
                </label>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 10 }}>
                  <label>
                    <div className="aeSmall">Included (one per line)</div>
                    <textarea
                      className="aeSearch"
                      style={{ minHeight: 90 }}
                      value={opt.includedText}
                      onChange={(e) => updateOption(optionIdx, { includedText: e.target.value })}
                    />
                  </label>
                  <label>
                    <div className="aeSmall">Excluded (one per line)</div>
                    <textarea
                      className="aeSearch"
                      style={{ minHeight: 90 }}
                      value={opt.excludedText}
                      onChange={(e) => updateOption(optionIdx, { excludedText: e.target.value })}
                    />
                  </label>
                </div>

                <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <strong>Groups / Areas</strong>
                  <button className="btn btnTiny btnGhost" type="button" onClick={() => addGroup(optionIdx)}>
                    + Add group
                  </button>
                </div>

                <div style={{ display: "grid", gap: 12, marginTop: 10 }}>
                  {opt.areas.map((g, groupIdx) => (
                    <div key={g.groupId} style={{ background: "rgba(15,23,42,.03)", borderRadius: 12, padding: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                        <div className="aeSmall">
                          <strong>{g.groupId}</strong>
                        </div>
                        <button
                          className="btn btnTiny btnGhost"
                          type="button"
                          onClick={() => removeGroup(optionIdx, groupIdx)}
                          disabled={opt.areas.length <= 1}
                        >
                          Remove group
                        </button>
                      </div>

                      <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1.8fr", gap: 12, marginTop: 8 }}>
                        <label>
                          <div className="aeSmall">Group Title</div>
                          <input
                            className="aeSearch"
                            value={g.title}
                            onChange={(e) => updateGroup(optionIdx, groupIdx, { title: e.target.value })}
                            placeholder="Area A — West Slope"
                          />
                        </label>
                        <label>
                          <div className="aeSmall">Notes</div>
                          <input
                            className="aeSearch"
                            value={g.notes}
                            onChange={(e) => updateGroup(optionIdx, groupIdx, { notes: e.target.value })}
                            placeholder="Work concentrated near ridge and transitions…"
                          />
                        </label>
                      </div>

                      <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <strong>Line Items</strong>
                        <button className="btn btnTiny btnGhost" type="button" onClick={() => addItem(optionIdx, groupIdx)}>
                          + Add item
                        </button>
                      </div>

                      <div style={{ display: "grid", gap: 10, marginTop: 10 }}>
                        {g.items.map((it, itemIdx) => (
                          <div key={it.itemId} style={{ background: "#fff", borderRadius: 12, border: "1px solid rgba(15,23,42,.08)", padding: 10 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
                              <div className="aeSmall">
                                <strong>{it.itemId}</strong>
                              </div>
                              <button
                                className="btn btnTiny"
                                type="button"
                                onClick={() => removeItem(optionIdx, groupIdx, itemIdx)}
                                disabled={g.items.length <= 1}
                              >
                                Remove
                              </button>
                            </div>

                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 8 }}>
                              <label>
                                <div className="aeSmall">Name</div>
                                <input
                                  className="aeSearch"
                                  value={it.name}
                                  onChange={(e) => updateItem(optionIdx, groupIdx, itemIdx, { name: e.target.value })}
                                  placeholder="Penetration flashing repair"
                                />
                              </label>

                              <label>
                                <div className="aeSmall">Category</div>
                                <select
                                  className="aeSearch"
                                  value={it.category}
                                  onChange={(e) => updateItem(optionIdx, groupIdx, itemIdx, { category: e.target.value })}
                                >
                                  <option>Repairs</option>
                                  <option>Maintenance</option>
                                  <option>Coating</option>
                                  <option>Replacement</option>
                                  <option>Mobilization</option>
                                  <option>Safety</option>
                                  <option>Equipment</option>
                                  <option>Misc</option>
                                </select>
                              </label>
                            </div>

                            <label style={{ display: "block", marginTop: 8 }}>
                              <div className="aeSmall">Description</div>
                              <textarea
                                className="aeSearch"
                                style={{ minHeight: 70 }}
                                value={it.description}
                                onChange={(e) => updateItem(optionIdx, groupIdx, itemIdx, { description: e.target.value })}
                              />
                            </label>

                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: 10, marginTop: 8 }}>
                              <label>
                                <div className="aeSmall">Qty</div>
                                <input
                                  className="aeSearch"
                                  value={it.qty}
                                  onChange={(e) => updateItem(optionIdx, groupIdx, itemIdx, { qty: e.target.value })}
                                />
                              </label>
                              <label>
                                <div className="aeSmall">Unit</div>
                                <select
                                  className="aeSearch"
                                  value={it.unit}
                                  onChange={(e) => updateItem(optionIdx, groupIdx, itemIdx, { unit: e.target.value })}
                                >
                                  <option value="ea">ea</option>
                                  <option value="lf">lf</option>
                                  <option value="sqft">sqft</option>
                                  <option value="sq">sq</option>
                                  <option value="hr">hr</option>
                                  <option value="day">day</option>
                                  <option value="lot">lot</option>
                                </select>
                              </label>
                              <label>
                                <div className="aeSmall">Unit Price</div>
                                <input
                                  className="aeSearch"
                                  value={it.unitPrice}
                                  onChange={(e) => updateItem(optionIdx, groupIdx, itemIdx, { unitPrice: e.target.value })}
                                />
                              </label>
                              <label>
                                <div className="aeSmall">Taxable</div>
                                <select
                                  className="aeSearch"
                                  value={it.taxable ? "yes" : "no"}
                                  onChange={(e) => updateItem(optionIdx, groupIdx, itemIdx, { taxable: e.target.value === "yes" })}
                                >
                                  <option value="yes">Yes</option>
                                  <option value="no">No</option>
                                </select>
                              </label>
                              <div>
                                <div className="aeSmall">Line Total</div>
                                <div style={{ padding: "10px 12px" }}>
                                  <strong>
                                    {Math.round(safeNum(it.qty) * safeNum(it.unitPrice)).toLocaleString()}
                                  </strong>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

              </div>
            );
          })}
        </div>
      </div>

      {/* SCHEDULE / PAYMENT / LEGAL */}
      <div className="aeTableWrap" style={{ padding: 16, marginBottom: 14 }}>
        <h3 style={{ margin: "0 0 8px" }}>Schedule & Payment</h3>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <label>
            <div className="aeSmall">Lead Time</div>
            <input
              className="aeSearch"
              value={form.schedule.leadTime}
              onChange={(e) => setField("schedule.leadTime", e.target.value)}
            />
          </label>
          <label>
            <div className="aeSmall">Duration</div>
            <input
              className="aeSearch"
              value={form.schedule.duration}
              onChange={(e) => setField("schedule.duration", e.target.value)}
            />
          </label>
          <label>
            <div className="aeSmall">Work Window</div>
            <input
              className="aeSearch"
              value={form.schedule.workWindow}
              onChange={(e) => setField("schedule.workWindow", e.target.value)}
            />
          </label>
        </div>

        <label style={{ display: "block", marginTop: 10 }}>
          <div className="aeSmall">Milestones (one per line, “Label — Notes”)</div>
          <textarea
            className="aeSearch"
            style={{ minHeight: 90 }}
            value={form.schedule.milestonesText}
            onChange={(e) => setField("schedule.milestonesText", e.target.value)}
          />
        </label>

        <div style={{ marginTop: 14, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <label>
            <div className="aeSmall">Deposit Required</div>
            <select
              className="aeSearch"
              value={form.payment.depositRequired ? "yes" : "no"}
              onChange={(e) => setField("payment.depositRequired", e.target.value === "yes")}
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </label>

          <label>
            <div className="aeSmall">Deposit Type</div>
            <select
              className="aeSearch"
              value={form.payment.depositType}
              onChange={(e) => setField("payment.depositType", e.target.value)}
            >
              <option value="percent">Percent</option>
              <option value="fixed">Fixed</option>
            </select>
          </label>

          <label>
            <div className="aeSmall">Deposit Value</div>
            <input
              className="aeSearch"
              value={form.payment.depositValue}
              onChange={(e) => setField("payment.depositValue", e.target.value)}
              placeholder="0.3 or 5000"
            />
          </label>
        </div>

        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label>
            <div className="aeSmall">Payment Methods (one per line)</div>
            <textarea
              className="aeSearch"
              style={{ minHeight: 80 }}
              value={form.payment.paymentMethodsText}
              onChange={(e) => setField("payment.paymentMethodsText", e.target.value)}
            />
          </label>
          <label>
            <div className="aeSmall">Invoicing Notes</div>
            <textarea
              className="aeSearch"
              style={{ minHeight: 80 }}
              value={form.payment.invoicing}
              onChange={(e) => setField("payment.invoicing", e.target.value)}
            />
          </label>
        </div>
      </div>

      <div className="aeTableWrap" style={{ padding: 16, marginBottom: 14 }}>
        <h3 style={{ margin: "0 0 8px" }}>Warranty & Legal</h3>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label>
            <div className="aeSmall">Warranty Type</div>
            <input
              className="aeSearch"
              value={form.warranty.type}
              onChange={(e) => setField("warranty.type", e.target.value)}
            />
          </label>
          <label>
            <div className="aeSmall">Warranty Term</div>
            <input
              className="aeSearch"
              value={form.warranty.term}
              onChange={(e) => setField("warranty.term", e.target.value)}
            />
          </label>
        </div>

        <label style={{ display: "block", marginTop: 10 }}>
          <div className="aeSmall">Warranty Details (one per line)</div>
          <textarea
            className="aeSearch"
            style={{ minHeight: 90 }}
            value={form.warranty.detailsText}
            onChange={(e) => setField("warranty.detailsText", e.target.value)}
          />
        </label>

        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label>
            <div className="aeSmall">Assumptions (one per line)</div>
            <textarea
              className="aeSearch"
              style={{ minHeight: 100 }}
              value={form.assumptionsText}
              onChange={(e) => setField("assumptionsText", e.target.value)}
            />
          </label>
          <label>
            <div className="aeSmall">Exclusions (one per line)</div>
            <textarea
              className="aeSearch"
              style={{ minHeight: 100 }}
              value={form.exclusionsText}
              onChange={(e) => setField("exclusionsText", e.target.value)}
            />
          </label>
        </div>

        <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <label>
            <div className="aeSmall">Clarifications (one per line)</div>
            <textarea
              className="aeSearch"
              style={{ minHeight: 100 }}
              value={form.clarificationsText}
              onChange={(e) => setField("clarificationsText", e.target.value)}
            />
          </label>
          <label>
            <div className="aeSmall">Terms (one per line)</div>
            <textarea
              className="aeSearch"
              style={{ minHeight: 100 }}
              value={form.termsText}
              onChange={(e) => setField("termsText", e.target.value)}
            />
          </label>
        </div>
      </div>

      <div className="aeSub" style={{ marginTop: 14 }}>
        Creating will write to <code>publicEstimates</code> and redirect you to edit.
      </div>
    </div>
  );
}
